"use client";

import { styleDrivers } from "@/utils/componentStyle";
import { cn } from "@/utils/utils";
import { Text } from "./text";
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  CircleAlertIcon
} from "lucide-react";
import * as React from "react";

type AlertVariant = "default" | "destructive" | "success" | "warning";

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  title?: string;
  description?: string;
}

const variantClasses: Record<AlertVariant, string> = styleDrivers.alert
  .variants as Record<AlertVariant, string>;
const variantIconMap = {
  default: <CircleAlertIcon />,
  destructive: <AlertCircleIcon />,
  success: <CheckCircleIcon />,
  warning: <AlertTriangleIcon />,
} as Record<AlertVariant, React.ReactNode>;

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = "default", title, description, ...props }, ref) => (
    <div
      ref={ref}
      role="alert"
      data-slot="alert"
      className={cn(
        styleDrivers.alert.root,
        styleDrivers.alert.variants[variant],
        className
      )}
      {...props}
    >
      {variantIconMap[variant as AlertVariant]}
      <div className="flex flex-col gap-1">
        <AlertTitle>{title}</AlertTitle>
        {description && <AlertDescription>{description}</AlertDescription>}
      </div>
    </div>
  )
);
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  <h5
    ref={ref}
    data-slot="alert-title"
    className={cn(
      "font-semibold text-body leading-none tracking-tight",
      className
    )}
    {...props}
  >
    {typeof children === "string" || typeof children === "number" ? (
      <Text as="p">{children}</Text>
    ) : (
      children
    )}
  </h5>
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="alert-description"
    className={cn("text-caption leading-relaxed", className)}
    {...props}
  >
    {typeof children === "string" || typeof children === "number" ? (
      <Text as="p">{children}</Text>
    ) : (
      children
    )}
  </div>
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
export default Alert;
